import React, { createContext, useContext, useEffect, useState } from 'react';
import { getContactInfo } from '../lib/api';

/**
 * Site-wide contact information (hotline, Zalo, WhatsApp, social URLs, email,
 * office address). Sourced from `GET /settings/contact-info`. If the request
 * fails or a field is empty, consumers MUST hide the corresponding UI element
 * — no hardcoded fallbacks anywhere.
 */
const ContactInfoContext = createContext({
  contactInfo: null,
  loading: true,
  error: null,
});

export const ContactInfoProvider = ({ children }) => {
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getContactInfo()
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.data || null;
        setContactInfo(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err);
        setContactInfo(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <ContactInfoContext.Provider value={{ contactInfo, loading, error }}>
      {children}
    </ContactInfoContext.Provider>
  );
};

export const useContactInfo = () => useContext(ContactInfoContext);

/** Build a Zalo deep link from API fields (prefers explicit zaloLink). */
export const getZaloHref = (info) => {
  if (!info) return '';
  if (info.zaloLink && info.zaloLink.trim()) return info.zaloLink;
  if (info.zaloNumber && info.zaloNumber.trim()) {
    return `https://zalo.me/${info.zaloNumber.replace(/[^0-9]/g, '')}`;
  }
  return '';
};

/** Build a WhatsApp deep link with optional message body. */
export const getWhatsAppHref = (info, message = '') => {
  if (!info?.whatsappNumber) return '';
  const num = info.whatsappNumber.replace(/[^0-9]/g, '');
  if (!num) return '';
  const text = message ? `?text=${encodeURIComponent(message)}` : '';
  return `https://wa.me/${num}${text}`;
};

/** Build a tel: href from the raw hotline phone. */
export const getTelHref = (info) => {
  if (!info?.hotlinePhone) return '';
  const num = info.hotlinePhone.replace(/[^+0-9]/g, '');
  return num ? `tel:${num}` : '';
};
